// Browsers cannot decode MPEG-1/2 program streams (.mpeg/.mpg) in a <video> element, so frame
// picking and screenshot capture fail for them. For those files we transcode a small MP4 preview
// in the browser with ffmpeg.wasm; the original file is still what gets uploaded. The preview only
// covers the first PREVIEW_MAX_SECONDS of the recording.
const MPEG_EXTENSIONS = ['.mpeg', '.mpg']

// ROV recordings can be very large, so the preview is deliberately bounded: the source is mounted
// read-only (never copied into wasm memory) and only a short, low-res, low-fps clip is encoded.
const PREVIEW_MAX_HEIGHT = 360
const PREVIEW_MAX_SECONDS = 60
const PREVIEW_FPS = 10
const PREVIEW_MAX_OUTPUT_BYTES = 64 * 1024 * 1024
const INPUT_MOUNT_DIR = '/input'

export const needsPlayablePreview = (file: Pick<File, 'name' | 'type'>): boolean => {
  const name = file.name?.toLowerCase() ?? ''
  return file.type === 'video/mpeg' || MPEG_EXTENSIONS.some(extension => name.endsWith(extension))
}

const transcodeToMp4Preview = async (file: File): Promise<Blob> => {
  const [{ FFmpeg }, { default: coreURL }, { default: wasmURL }] = await Promise.all([
    import('@ffmpeg/ffmpeg'),
    import('@ffmpeg/core?url'),
    import('@ffmpeg/core/wasm?url'),
  ])

  const ffmpeg = new FFmpeg()
  try {
    await ffmpeg.load({ coreURL, wasmURL })
    await ffmpeg.createDir(INPUT_MOUNT_DIR)
    // WORKERFS reads the File lazily in chunks, so the whole source is never held in wasm memory.
    await ffmpeg.mount('WORKERFS' as never, { files: [file] }, INPUT_MOUNT_DIR)
    const exitCode = await ffmpeg.exec([
      '-i',
      `${INPUT_MOUNT_DIR}/${file.name}`,
      '-t',
      String(PREVIEW_MAX_SECONDS),
      '-an',
      '-vf',
      `fps=${PREVIEW_FPS},scale=-2:'min(${PREVIEW_MAX_HEIGHT},ih)'`,
      '-c:v',
      'libx264',
      '-preset',
      'ultrafast',
      '-crf',
      '30',
      '-pix_fmt',
      'yuv420p',
      '-fs',
      String(PREVIEW_MAX_OUTPUT_BYTES),
      '-movflags',
      '+faststart',
      'preview.mp4',
    ])
    if (exitCode !== 0) throw new Error(`ffmpeg exited with code ${exitCode}`)
    const data = (await ffmpeg.readFile('preview.mp4')) as Uint8Array
    return new Blob([data], { type: 'video/mp4' })
  } finally {
    ffmpeg.terminate()
  }
}

const previewCache = new WeakMap<File, Promise<Blob>>()

export const getPlayableVideoBlob = (file: File): Promise<Blob> => {
  if (!needsPlayablePreview(file)) return Promise.resolve(file)

  let preview = previewCache.get(file)
  if (!preview) {
    preview = transcodeToMp4Preview(file)
    preview.catch(() => previewCache.delete(file))
    previewCache.set(file, preview)
  }
  return preview
}
